import useMap from "@/lib/map/useMap";
import {
    moveLayersToTop,
    updateLayerOpacity,
    updateLayerVisibility,
} from "@/lib/utils/map";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, Slider } from "@heroui/react";
import { useState } from "react";

function SimpleLayer({
    name,
    layerNames,
    defaultVisible = false,
    disabled = false,
    children,
    onDownload,
    onValueChange,
}: {
    name: string;
    layerNames: string[];
    defaultVisible?: boolean;
    disabled?: boolean;
    children?: (isChecked: boolean) => React.ReactNode;
    onDownload?: () => void;
    onValueChange?: (value: boolean) => void;
}) {
    const { map, mapLoaded } = useMap();
    const [isChecked, setIsChecked] = useState(defaultVisible ?? false);
    const [opacity, setOpacity] = useState(0.8);

    const handleCheckboxChange = (checked: boolean) => {
        setIsChecked(checked);
        if (!map || !mapLoaded) return;
        updateLayerVisibility(map, layerNames, checked);
        if (checked) {
            moveLayersToTop(map, layerNames);
        }
        if (onValueChange) {
            onValueChange(checked);
        }
    };

    const handleOpacityChange = (opacity: number | number[]) => {
        setOpacity(opacity as number);
        if (!map || !mapLoaded) return;
        updateLayerOpacity(map, layerNames, opacity as number);
    };

    const handleBringToTop = () => {
        if (!map || !mapLoaded) return;
        moveLayersToTop(map, layerNames);
    };

    return (
        <div
            className={`px-2 pt-1  ${disabled ? "opacity-50" : ""} overflow-hidden scrollbar-hide`}
        >
            <div className="flex flex-row items-center justify-between w-full gap-2">
                <Checkbox
                    classNames={{
                        base: "p-0",
                    }}
                    isSelected={isChecked}
                    onValueChange={handleCheckboxChange}
                    isDisabled={disabled}
                    className="text-xs "
                    aria-label={name}
                ></Checkbox>
                <div className="text-sm flex-grow truncate ml-1" title={name}>
                    {name}
                </div>
                <div className="flex flex-row items-center gap-1 flex-shrink-0">
                    {isChecked && (
                        <Button
                            className="px-2 py-0"
                            color="primary"
                            variant="light"
                            size="sm"
                            onPress={handleBringToTop}
                            isDisabled={disabled}
                        >
                            Bring to top
                        </Button>
                    )}
                    {onDownload && (
                        <div className="flex-shrink-0">
                            <Button
                                color="primary"
                                variant="light"
                                size="sm"
                                isIconOnly
                                className="text-[0.6rem]"
                                onPress={onDownload}
                            >
                                <FontAwesomeIcon icon={faDownload} />
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            {isChecked && (
                <Slider
                    size="sm"
                    aria-label="Opacity"
                    minValue={0}
                    maxValue={1}
                    step={0.01}
                    value={opacity}
                    onChange={handleOpacityChange}
                    isDisabled={disabled}
                    className="py-1"
                />
            )}
            {children?.(isChecked)}
        </div>
    );
}

export default SimpleLayer;
